import React, { FC, Suspense, useState, useEffect } from 'react';
import {
  Box,
  ButtonSize,
  Heading,
  Button,
  ButtonAppearance,
  Text,
  Icon,
  IconSize,
  TextArea,
  ToastType,
  ToastAction,
  useToastsDispatch,
  Spinner,
} from '@bechtel/gds';
import { Formik, FormikProps, Field } from 'formik';
import { t, Trans } from '@lingui/macro';
import {
  UpdateCommodityLocationDescriptionErrorExt,
  WeldLocationProps,
  WeldLocationDescriptionFormikState,
} from './types';
import { Map, Marker, MarkerType } from '~/components/organisms';
import { useEnvProvider, SuspenseLoader, ErrorBoundary, useConfig, useWeldAssignees } from '@bechtel/client-common';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import PipeIcon from '@bechtel/client-assets/dist/images/pipe-icon.svg';
import { MapContainerStyled, LocationDescStyled, FieldError } from './styled';
import {
  UpdateCommodityLocationDescriptionInput,
  UpdateCommodityLocationDescriptionPayload,
  CommodityAssignee,
} from '@bechtel/types';
import { UPDATE_COMMODITY_LOCATION_DESCRIPTION } from '@bechtel/client-gql';
import { useMutation } from '@apollo/client';
import { get } from 'lodash';
import UpdateCommodityLocationDescError from './error';
import { object, string } from 'yup';
import { useWeldPermissions } from '~/hooks';

const locationDescriptionUpdateToast = () => {
  return (
    <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        <Trans>Location description saved successfully.</Trans>
      </Text>
    </Box>
  );
};

const locationDescriptionValidationSchema = object().shape({
  locationDesc: string().max(2000, t`Too Long!`),
});

export const WeldLocation: FC<WeldLocationProps> = ({ weldId, location, locationDescription, commodityAssignees }) => {
  const { envConfig } = useEnvProvider();
  const { features } = useConfig();
  const [showMap, setShowMap] = useState<boolean>(false);
  const [canEditLocationDesc, setCanEditLocationDesc] = useState<boolean>(false);
  const [
    updateLocationDescriptionError,
    setUpdateLocationDescriptionError,
  ] = useState<UpdateCommodityLocationDescriptionErrorExt | null>(null);
  const assignedCrewMembers: CommodityAssignee[] = useWeldAssignees(commodityAssignees);
  const { canApproveWeld, canClaimWeld } = useWeldPermissions(assignedCrewMembers);
  const dispatchToast = useToastsDispatch();

  const initialFormikValues: WeldLocationDescriptionFormikState = {
    locationDesc: locationDescription || '',
  };

  const [updateLocationDescription, { loading: mutationLoading }] = useMutation<
    { updateCommodityLocationDescriptionPayload: UpdateCommodityLocationDescriptionPayload },
    { input: UpdateCommodityLocationDescriptionInput }
  >(UPDATE_COMMODITY_LOCATION_DESCRIPTION, {
    onCompleted: (data) => {
      const updateError = get(data, 'updateCommodityLocationDescription.error');
      if (updateError) {
        setUpdateLocationDescriptionError(updateError);
      } else {
        const updatedData: any = data;
        setUpdateLocationDescriptionError(null);
        const toastAction: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: locationDescriptionUpdateToast,
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(toastAction);
        setCanEditLocationDesc(false);
      }
    },
    onError: (error) => {
      setUpdateLocationDescriptionError(error);
    },
  });

  const closeModal = () => {
    setUpdateLocationDescriptionError(null);
  };

  const mapCenter: google.maps.LatLngLiteral = {
    lat: location?.latitude,
    lng: location?.longitude,
  };

  const weldMarker: Marker = {
    id: weldId,
    markerType: MarkerType.PIPING,
    icon: PipeIcon,
    position: {
      lat: location?.latitude,
      lng: location?.longitude,
    },
  };

  return (
    <>
      <Box ml={{ sm: '700' }} mr={{ sm: '500' }} my={{ sm: '700' }} flexDirection={{ sm: 'column' }}>
        <Box mb={{ sm: '600' }}>
          <Heading color="grey.1000" size="600" weight="600">
            <Trans>Location</Trans>
          </Heading>
        </Box>
        {!!location?.latitude && !!location?.longitude && (
          <Box mb={{ sm: '400' }}>
            <Heading color="grey.1000" size="400" weight="600">
              {location?.latitude ? location?.latitude : ''}, {location?.longitude ? location?.longitude : ''}
              &nbsp;—&nbsp;
              <Trans>Welded Location</Trans>
            </Heading>
          </Box>
        )}
        {location?.elevation && (
          <Box mb={{ sm: '450' }}>
            <Text color="grey.700" size="300">
              Elevation: {location.elevation}
            </Text>
          </Box>
        )}
        {features?.showMapView && !!location?.latitude && !!location?.longitude && (
          <Box
            mb={{ sm: '500' }}
            alignItems={{ sm: 'flex-end' }}
            style={{ cursor: 'pointer' }}
            onClick={() => setShowMap(!showMap)}
          >
            <Text size="400" color="bechtel.blue.700">
              <Trans>View on Map</Trans>
            </Text>
            <Box ml={{ sm: '200' }}>
              <Icon
                icon={showMap ? 'chevron-down' : 'chevron-up'}
                fillColor="bechtel.blue.800"
                size={IconSize.XSMALL}
              />
            </Box>
          </Box>
        )}
        {showMap && (
          <MapContainerStyled>
            <Map
              apiKey={envConfig?.mapApiKey}
              zoom={16}
              center={mapCenter}
              markers={[weldMarker]}
              // setting the value using enum from google.maps.MapTypeId throwing google undefined error.needs debugging. With string, it's working fine.
              mapTypeId="satellite"
              hasFilter={false}
            />
          </MapContainerStyled>
        )}
      </Box>
      <Formik
        initialValues={initialFormikValues}
        validationSchema={locationDescriptionValidationSchema}
        enableReinitialize={true}
        onSubmit={async (values) => {
          const UpdateCommodityLocationDescriptionInput: UpdateCommodityLocationDescriptionInput = {
            commodityId: weldId,
            locationDescription: values.locationDesc!,
          };
          await updateLocationDescription({
            variables: {
              input: UpdateCommodityLocationDescriptionInput,
            },
          });
        }}
      >
        {(props: FormikProps<WeldLocationDescriptionFormikState>) => {
          const { handleSubmit, isValid, errors } = props;
          return (
            <Box ml={{ sm: '700' }} mr={{ sm: '500' }} my={{ sm: '700' }} flexDirection={{ sm: 'column' }}>
              {!canEditLocationDesc && (
                <Box mb={{ sm: '400' }} pr={{ sm: '700' }} justifyContent={{ sm: 'space-between' }}>
                  <Text
                    size="400"
                    color="grey.700"
                    family="graphik"
                    style={{ lineHeight: 1.7, wordBreak: 'break-word' }}
                  >
                    {locationDescription}
                  </Text>
                  {(canApproveWeld || canClaimWeld) && (
                    <Icon
                      style={{ cursor: 'pointer' }}
                      onClick={() => setCanEditLocationDesc(true)}
                      icon="edit"
                      fillColor="bechtel.blue.800"
                      size={IconSize.MEDIUM}
                    />
                  )}
                </Box>
              )}
              {canEditLocationDesc && (
                <Box flexDirection={{ sm: 'column' }}>
                  <LocationDescStyled>
                    <Field name="locationDesc">
                      {({ field }) => <TextArea maxLength={2000} rows={4} {...field} />}
                    </Field>

                    <Box ml={{ sm: '500' }} justifyContent={{ sm: 'flex-end' }} style={{ width: '18%' }}>
                      <Button
                        onClick={() => handleSubmit()}
                        size={ButtonSize.SMALL}
                        appearance={ButtonAppearance.PRIMARY}
                        disabled={mutationLoading || !isValid}
                      >
                        <Box px={{ sm: '400' }}>
                          <Box mr={{ sm: '400' }}>
                            <Icon icon="check" fillColor="white" size={IconSize.SMALL} />
                          </Box>
                          <Text size="300" color="white" family="graphik">
                            {mutationLoading && <Spinner />}
                            <Trans>Ok</Trans>
                          </Text>
                        </Box>
                      </Button>
                    </Box>
                  </LocationDescStyled>
                  {errors?.locationDesc && (
                    <Box p={{ sm: '500' }}>
                      <FieldError>{errors.locationDesc}</FieldError>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          );
        }}
      </Formik>
      {updateLocationDescriptionError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <UpdateCommodityLocationDescError error={updateLocationDescriptionError} close={closeModal} />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};
